import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Copy, RefreshCw, ArrowRightLeft, Thermometer } from 'lucide-react';
import { toast } from 'sonner';

const TemperatureConverterTool = () => {
  const [celsius, setCelsius] = useState('0');
  const [fahrenheit, setFahrenheit] = useState('32');
  const [kelvin, setKelvin] = useState('273.15');

  const cToF = (c) => ((parseFloat(c) * 9/5) + 32).toFixed(2);
  const cToK = (c) => (parseFloat(c) + 273.15).toFixed(2);
  const fToC = (f) => ((parseFloat(f) - 32) * 5/9).toFixed(2);
  const fToK = (f) => (((parseFloat(f) - 32) * 5/9) + 273.15).toFixed(2);
  const kToC = (k) => (parseFloat(k) - 273.15).toFixed(2);
  const kToF = (k) => (((parseFloat(k) - 273.15) * 9/5) + 32).toFixed(2);

  const handleCelsiusChange = (value) => {
    setCelsius(value);
    if (value && !isNaN(value)) {
      setFahrenheit(cToF(value));
      setKelvin(cToK(value));
    }
  };

  const handleFahrenheitChange = (value) => {
    setFahrenheit(value);
    if (value && !isNaN(value)) {
      setCelsius(fToC(value));
      setKelvin(fToK(value));
    }
  };

  const handleKelvinChange = (value) => {
    setKelvin(value);
    if (value && !isNaN(value)) {
      setCelsius(kToC(value));
      setFahrenheit(kToF(value));
    }
  };

  const copyValue = (value, unit) => {
    navigator.clipboard.writeText(`${value}${unit}`);
    toast.success('Copied!');
  };

  const presets = [
    { c: -40, label: '-40°C = -40°F' },
    { c: 0, label: 'Water freezes' },
    { c: 20, label: 'Room temp' },
    { c: 37, label: 'Body temp' },
    { c: 100, label: 'Water boils' },
  ];

  return (
    <div className="space-y-6" data-testid="temperature-converter-tool">
      <div className="grid md:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl bg-muted/30 border border-border">
          <label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
            <Thermometer className="w-4 h-4 text-blue-400" />
            Celsius (°C)
          </label>
          <div className="flex gap-2">
            <Input
              type="number"
              value={celsius}
              onChange={(e) => handleCelsiusChange(e.target.value)}
              className="font-mono"
            />
            <Button onClick={() => copyValue(celsius, '°C')} variant="outline" size="icon">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-4 rounded-xl bg-muted/30 border border-border">
          <label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
            <Thermometer className="w-4 h-4 text-red-400" />
            Fahrenheit (°F)
          </label>
          <div className="flex gap-2">
            <Input
              type="number"
              value={fahrenheit}
              onChange={(e) => handleFahrenheitChange(e.target.value)}
              className="font-mono"
            />
            <Button onClick={() => copyValue(fahrenheit, '°F')} variant="outline" size="icon">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-4 rounded-xl bg-muted/30 border border-border">
          <label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
            <Thermometer className="w-4 h-4 text-purple-400" />
            Kelvin (K)
          </label>
          <div className="flex gap-2">
            <Input
              type="number"
              value={kelvin}
              onChange={(e) => handleKelvinChange(e.target.value)}
              className="font-mono"
            />
            <Button onClick={() => copyValue(kelvin, 'K')} variant="outline" size="icon">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 rounded-xl bg-muted/30 border border-border">
        <h3 className="font-semibold mb-3">Quick Reference</h3>
        <div className="flex flex-wrap gap-2">
          {presets.map(preset => (
            <Button
              key={preset.c}
              variant="outline"
              size="sm"
              onClick={() => handleCelsiusChange(preset.c.toString())}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
        <h3 className="font-semibold mb-2">Formulas</h3>
        <div className="text-sm font-mono text-muted-foreground space-y-1">
          <p>°F = (°C × 9/5) + 32</p>
          <p>°C = (°F - 32) × 5/9</p>
          <p>K = °C + 273.15</p>
        </div>
      </div>

      <div className="flex gap-2 justify-center">
        <Button onClick={() => handleCelsiusChange('0')} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Reset
        </Button>
      </div>
    </div>
  );
};

export default TemperatureConverterTool;
